import { useEffect, useMemo, useRef, useState } from "react";
import { Grid, Skeleton, Stack } from "@mui/material";
import { useTranslation } from "react-i18next";
import { col6 } from "../../../../common/utils";
import NumericValueWidget from "../../../../components/charts/NumericValueWidget";
import DropdownSelect from "../../../../components/common/DropdownSelect";
import { View } from "../../../../components/layout/View";
import PaperCard from "../../../../components/widgets/PaperCard";
import PaperCardHeader from "../../../../components/widgets/PaperCard/PaperCardHeader";
import { PageType } from "../../../../config/page.types";
import { useFiltersRegistry } from "../../../../context/FiltersProvider";
import { usePageConfigurationState } from "../../../../context/PageConfigrations/usePageConfigurationState";
import {
	ConsumptionPageDTO,
	ConsumptionPageItemDTO,
	NodesPageDTO,
	ResultsItemsListPayload,
} from "../../../../datasources/api-client/data-contracts";
import { ApiRoutes } from "../../../../datasources/networking/apiRoutes";
import { usePeriodFilter } from "../../../../hooks/usePreferences";
import { useReadResource } from "../../../../hooks/useResource";
import { useOperationalUnitsData } from "../useOperationalUnitsData";
import AccordionTable from "./AccordionTable";
import { GreenrouterColumn, greenrouterColumns, GreenrouterRow } from "./data";

// Sorting model used by the table
type SortModel = {
	field?: keyof GreenrouterRow;
	direction?: "asc" | "desc";
};

// Fields supported by the API sorting
type SortableApiField = "id" | "description" | "type" | "city" | "country" | "area" | "emissions";

// Only emission available for this page
export type EmissionsKeyOperationalUnits = "co2Wtw";

// Map FE column names to API fields when needed
const SORT_OVERRIDE: Partial<Record<keyof GreenrouterRow, SortableApiField>> = {
	typeDescription: "type",
};

const OperationalUnitsGreenrouter = () => {
	const containerRef = useRef<HTMLDivElement | null>(null);
	const { t } = useTranslation("feature.units");

	const { formattedFrom: from, formattedTo: to } = usePeriodFilter();

	const { appliedFilters } = useFiltersRegistry();

	// Page configuration
	const [config, setConfig] = usePageConfigurationState(PageType.ResultsOperationalUnitsGreenRouter, {
		emissionsKey: "co2Wtw",
		page: 0,
		pageSize: 10,
		sortField: undefined,
		sortDirection: undefined,
		columnVisibilityModel: {},
	});

	// Pagination for node consumptions
	const [nodeConsumptionPagination, setNodeConsumptionPagination] = useState(0);

	// Currently opened node
	const [selectedNodeId, setSelectedNodeId] = useState<string | false>(false);

	// Accumulated consumptions for "load more"
	const [allConsumptions, setAllConsumptions] = useState<ConsumptionPageDTO["items"]>([]);

	// Generate columns using visibility stored in page configuration
	// Save all the state of the columns
	const columns = useMemo(() => {
		const baseColumns = greenrouterColumns(t);

		return baseColumns.map((col) => ({
			...col,
			visible: config.columnVisibilityModel[col.field] ?? col.visible,
		}));
	}, [t, config.columnVisibilityModel]);

	// Handle change of the columns
	const handleColumnsChange = (updatedColumns: GreenrouterColumn[]) => {
		const visibilityModel: Record<string, boolean> = {};

		updatedColumns.forEach((col) => {
			visibilityModel[col.field] = col.visible;
		});

		setConfig({
			columnVisibilityModel: visibilityModel,
		});
	};

	const sortModel: SortModel = {
		field: config.sortField as keyof GreenrouterRow | undefined,
		direction: config.sortDirection,
	};

	// Dropdown emissions selector
	const dropdownSelectEmissions = [{ key: "co2Wtw", label: t("common:wtw select") }];

	const handleEmissionChange = (key: string) => {
		setConfig({ emissionsKey: key as EmissionsKeyOperationalUnits });
	};

	// Base payload used by API calls
	const payload = useMemo(() => ({ ...appliedFilters.payload }), [appliedFilters]);

	const appliedFiltersKey = useMemo(() => JSON.stringify(appliedFilters), [appliedFilters]);

	useEffect(() => {
		if (config.page !== 0) {
			setConfig({ page: 0 });
		}
	}, [appliedFiltersKey]);

	// Emissions data for widgets
	const { emissions } = useOperationalUnitsData({
		payload,
		tab: "greenrouterUnits",
		from,
		to,
	});

	// Handle table sorting
	const handleSortChange = (field: keyof GreenrouterRow, direction?: "asc" | "desc") => {
		if (!direction) {
			setConfig({ sortField: undefined, sortDirection: undefined });
			return;
		}

		setConfig({
			sortField: field,
			sortDirection: direction,
		});
	};

	// Resolve API sorting field
	const sortBy = sortModel.field
		? (SORT_OVERRIDE[sortModel.field] ?? (sortModel.field as SortableApiField))
		: undefined;

	const sortDir = sortModel.direction;

	// Fetch operational units list
	const {
		data: dataOperationalUnits,
		isLoading: isLoadingOperationalUnits,
		reload: reloadOperationalUnits,
	} = useReadResource<ResultsItemsListPayload, NodesPageDTO>(
		{
			route: ApiRoutes.ResultGreerouterSitesList,
			query: {
				page: config.page,
				pageSize: config.pageSize,
				...(sortBy && { sortBy }),
				...(sortDir && { sortDir }),
			},
		},
		{
			readMethod: "post",
		},
		{ ...payload }
	);

	// Pagination handlers
	const handlePageChange = (newPage: number) => {
		setConfig({ page: newPage });
	};

	const handlePageSizeChange = (newPageSize: number) => {
		setConfig({
			page: 0,
			pageSize: newPageSize,
		});
	};

	// When accordion opens load node consumptions
	const handleAccordionOpen = (id: string) => {
		setNodeConsumptionPagination(0);

		setAllConsumptions((prev: ConsumptionPageItemDTO[]) => {
			if (selectedNodeId === id) return prev;
			return [];
		});

		setSelectedNodeId(id);
	};

	// Fetch node consumptions
	const {
		data: dataNodeConsumptions,
		isLoading: loadingConsumptions,
		reload: reloadConsumptions,
	} = useReadResource<ResultsItemsListPayload, ConsumptionPageDTO>(
		{
			route: ApiRoutes.ResultsSitesNodeDetailsConsumption,
			path: { id: selectedNodeId, page: nodeConsumptionPagination, pageSize: 10 },
			query: { id: selectedNodeId, page: nodeConsumptionPagination },
		},
		{
			readMethod: "post",
			disabled: !selectedNodeId,
		},
		{ ...payload }
	);

	// Check if more pages exist
	const hasMore = dataNodeConsumptions && dataNodeConsumptions.page + 1 < dataNodeConsumptions.pages;

	const handleLoadMore = () => {
		if (loadingConsumptions) return;
		if (!hasMore) return;

		setNodeConsumptionPagination((prev) => prev + 1);
	};

	// Merge paginated consumptions
	useEffect(() => {
		if (!dataNodeConsumptions?.items) return;

		setAllConsumptions((prev: ConsumptionPageItemDTO[]) => {
			if (nodeConsumptionPagination === 0) return dataNodeConsumptions.items;

			const newItems = dataNodeConsumptions.items.filter(
				(item: ConsumptionPageItemDTO) => !prev.some((p) => p.id === item.id)
			);

			return [...prev, ...newItems];
		});
	}, [dataNodeConsumptions, nodeConsumptionPagination]);

	// Data passed to accordion table
	const consumptionsData = dataNodeConsumptions
		? {
				...dataNodeConsumptions,
				items: allConsumptions,
				page: dataNodeConsumptions.page ?? 0,
				pageSize: dataNodeConsumptions.pageSize ?? 10,
				pages: dataNodeConsumptions.pages ?? 1,
				itemsCount: dataNodeConsumptions.itemsCount ?? allConsumptions.length,
				pageSizes: dataNodeConsumptions.pageSizes ?? [10],
				total: dataNodeConsumptions.total ?? null,
			}
		: {
				items: allConsumptions,
				page: 0,
				pageSize: 10,
				pages: 1,
				itemsCount: allConsumptions.length,
				pageSizes: [10],
				total: null,
			};

	return (
		<View pageType={PageType.ResultsOperationalUnitsGreenRouter} sx={{ maxWidth: "100vw", overflowX: "hidden" }}>
			<Grid container spacing={2}>
				<Grid size={12}>
					<Stack alignItems={"flex-end"}>
						<DropdownSelect
							text={t("common:display for")}
							value={config.emissionsKey}
							loading={emissions.sites.isLoading}
							onChange={handleEmissionChange}
							dropdownItems={dropdownSelectEmissions}
							variant="standard"
						/>
					</Stack>
				</Grid>

				<Grid size={col6} minHeight={150}>
					<NumericValueWidget
						title={t("overview/total emissions")}
						value={emissions.sites.data?.greenRouter?.current.co2Wtw.value}
						measure={emissions.sites.data?.greenRouter?.current.co2Wtw.measure}
						isLoading={emissions.sites.isLoading}
						onReload={emissions.sites.reload}
					/>
				</Grid>

				<Grid size={col6} minHeight={150}>
					<NumericValueWidget
						title={t("overview/no operational units")}
						value={emissions.sites.data?.greenRouter?.current.count}
						isLoading={emissions.sites.isLoading}
						onReload={emissions.sites.reload}
					/>
				</Grid>

				<Grid size={12} minHeight={400}>
					{isLoadingOperationalUnits ? (
						<Skeleton variant="rounded" height="100%" width="100%" />
					) : (
						<PaperCard
							ref={containerRef}
							header={
								<PaperCardHeader
									title={t("greenrouterList/list of Greenrouter operating units")}
									isLoading={isLoadingOperationalUnits}
									onReload={reloadOperationalUnits}
									containerRef={containerRef}
								/>
							}
							content={
								<AccordionTable
									reload={reloadOperationalUnits}
									page={dataOperationalUnits?.page || config.page}
									pages={dataOperationalUnits?.pages || 0}
									pageSize={dataOperationalUnits?.pageSize || config.pageSize}
									itemsCount={dataOperationalUnits?.itemsCount || 0}
									items={dataOperationalUnits?.items || []}
									pageSizes={dataOperationalUnits?.pageSizes || [10, 25, 50]}
									onPageChange={handlePageChange}
									onPageSizeChange={handlePageSizeChange}
									onAccordionOpen={handleAccordionOpen}
									loadingConsumptions={loadingConsumptions}
									consumptionsData={consumptionsData}
									reloadConsumptions={reloadConsumptions}
									handleLoadMore={handleLoadMore}
									hasMore={hasMore}
									columns={columns}
									sortModel={sortModel}
									onSortChange={handleSortChange}
									onColumnsChange={handleColumnsChange}
								/>
							}
						/>
					)}
				</Grid>
			</Grid>
		</View>
	);
};

export default OperationalUnitsGreenrouter;
